import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { initialContasPagarFiltersValues, useContasPagarListaFilters } from "../hooks/contas_pagar_lista/filters";
import { useContasPagarListaPagination } from "../hooks/contas_pagar_lista/pagination";
import { ContasPagarLista } from "../../domain/models/contas_pagar_lista";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

type LoadContasPagarListaState = {

    idsCentroCusto?: number[];
    idsOrdemCompra?: number[];
    idsBanco?: number[];
    idsSubconta?: number[];
    idsFornecedor?: number[];
    idsFuncionario?: number[];
    idsFuncionarioPago?: number[];
    idsProdutoServico?: number[];
    idsPgtoMedicao?: number[];

    titulo?: string | null;
    valor?: number | null;
    nrContasPagar?: number | null;
    nrNotaFiscal?: string | null;
    nrNotaFiscalEspecial?: string | null;
    formaPagamento?: string | null;
    status?: number | null;

    dataPagarDe?: Date | null;
    dataPagarAte?: Date | null;
    dataPagoDe?: Date | null;
    dataPagoAte?: Date | null;
    dataCriacaoDe?: Date | null;
    dataCriacaoAte?: Date | null;

    page?: number;
    rowsPerPage?: number;

}


export const useContasPagarDeletadasViewModel = () => {

    const {
        reports: {
            getReportContaPagarUseCase,
        },
        financeiro: {
            getAllContasPagarDeletadasUseCase,
            recuperacaoContasPagarUseCase,

        },
        commons: {
            getCentroCustoDropDownUseCase,
            getBancosPagamentoDropDownUseCase,
            getOrdemComprasDropDownUseCase,
            getSubcontasDropDownUseCase,
            getFornecedorDropDownUseCase,
            getFuncionariosDropDownUseCase,
            getMedicaoTeceirizacaoDropDownUseCase,
            getProdutoServicoDropDownUseCase

        },

    } = useDependencyInjection();

    const navigate = useNavigate();

    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const [reportViewerState, startReportViewerProcess] = useReportViewer();

    const debouncedRef = useCreateDebounceHandler(1500);

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [ContasPagarLista, setContasPagarLista] = useState<ContasPagarLista[]>();

    const filters = useContasPagarListaFilters();
    const pagination = useContasPagarListaPagination();

    //DROPDOWN ESTADOS

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlBanco, setDdlBanco] = useState<DropDown[]>();
    const [ddlOrdemCompra, setDdlOrdemCompra] = useState<DropDown[]>();
    const [ddlSubconta, setDdlSubconta] = useState<DropDown[]>();
    const [ddlFornecedor, setDdlFornecedor] = useState<DropDown[]>();
    const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>();
    const [ddlProdutoServico, setDdlProdutoServico] = useState<DropDown[]>();
    const [ddlPgtoMedicao, setDdlPgtoMedicao] = useState<DropDown[]>();


    //#region CARREGAMENTO.

    const loadContasPagarListaState = useCallback(async (funcState?: LoadContasPagarListaState) => {
        try {
            const filteredTitulo = evaluateParamValue(funcState?.titulo, filters.titulo);
            const filteredValor = evaluateParamValue(funcState?.valor, filters.valor);
            const filteredNrContasPagar = evaluateParamValue(funcState?.nrContasPagar, filters.nrContasPagar);
            const filteredNrNotaFiscal = evaluateParamValue(funcState?.nrNotaFiscal, filters.nrNotaFiscal);
            const filteredNrNotaFiscalEspecial = evaluateParamValue(funcState?.nrNotaFiscalEspecial, filters.nrNotaFiscalEspecial);
            const filteredFormaPagamento = evaluateParamValue(funcState?.formaPagamento, filters.formaPagamento);
            const filteredStatus = evaluateParamValue(funcState?.status, filters.status);

            const filteredDataPagarDe = evaluateParamValue(funcState?.dataPagarDe, filters.dataPagarDe);
            const filteredDataPagarAte = evaluateParamValue(funcState?.dataPagarAte, filters.dataPagarAte);
            const filteredDataPagoDe = evaluateParamValue(funcState?.dataPagoDe, filters.dataPagoDe);
            const filteredDataPagoAte = evaluateParamValue(funcState?.dataPagoAte, filters.dataPagoAte);
            const filteredDataCriacaoDe = evaluateParamValue(funcState?.dataCriacaoDe, filters.dataCriacaoDe);
            const filteredDataCriacaoAte = evaluateParamValue(funcState?.dataCriacaoAte, filters.dataCriacaoAte);

            const filteredIdsCentroCusto = funcState?.idsCentroCusto ?? filters.idsCentroCusto;
            const filteredIdsOrdemCompra = funcState?.idsOrdemCompra ?? filters.idsOrdemCompra;
            const filteredIdsBanco = funcState?.idsBanco ?? filters.idsBanco;
            const filteredIdsSubconta = funcState?.idsSubconta ?? filters.idsSubconta;
            const filteredIdsFornecedor = funcState?.idsFornecedor ?? filters.idsFornecedor;
            const filteredIdsFuncionario = funcState?.idsFuncionario ?? filters.idsFuncionario;
            const filteredIdsProdutoServico = funcState?.idsProdutoServico ?? filters.idsProdutoServico;
            const filteredIdsPgtoMedicao = funcState?.idsPgtoMedicao ?? filters.idsPgtoMedicao;

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllContasPagarDeletadasUseCase.getAllContasPagarDeletadas(
                {
                    titulo: filteredTitulo ?? "",
                    valor: filteredValor,
                    nrContasPagar: filteredNrContasPagar,
                    nrNotaFiscal: filteredNrNotaFiscal,
                    nrNotaFiscalEspecial: filteredNrNotaFiscalEspecial,
                    formaPagamento: filteredFormaPagamento,
                    status: filteredStatus,

                    dataPagarDe: filteredDataPagarDe,
                    dataPagarAte: filteredDataPagarAte,
                    dataPagoDe: filteredDataPagoDe,
                    dataPagoAte: filteredDataPagoAte,
                    dataCriacaoDe: filteredDataCriacaoDe,
                    dataCriacaoAte: filteredDataCriacaoAte,

                    idsCentroCusto: filteredIdsCentroCusto,
                    idsOrdemCompra: filteredIdsOrdemCompra,
                    idsBanco: filteredIdsBanco,
                    idsSubconta: filteredIdsSubconta,
                    idsFornecedor: filteredIdsFornecedor,
                    idsFuncionario: filteredIdsFuncionario,
                    idsProdutoServico: filteredIdsProdutoServico,
                    idsPgtoMedicao: filteredIdsPgtoMedicao,
                },
                filteredPage,
                filteredRowsPerPage
            );

            setContasPagarLista(getAllContasPagarDeletadasUseCase.items);
            pagination.setRowsPerPage(getAllContasPagarDeletadasUseCase.pageSize);
            pagination.setTotalItems(getAllContasPagarDeletadasUseCase.totalItems);
        } catch (e: unknown) {
            setContasPagarLista([]);
            throw new Error("Falha ao buscar contas a pagar!");
        }
    }, [
        filters,
        pagination,
        getAllContasPagarDeletadasUseCase,
    ]);

    const loadCentroCustoDropDown = useCallback(async () => {

        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }

    }, [getCentroCustoDropDownUseCase]);

    const loadBancosPagamentoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBanco(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar bancos!'));
        } finally {
            setPageLoading(false);
        }
    }, [getBancosPagamentoDropDownUseCase]);

    const loadOrdemCompraDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getOrdemComprasDropDownUseCase.getOrdemComprasDropDown();
            setDdlOrdemCompra(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar ordens de compra!'));
        } finally {
            setPageLoading(false);
        }
    }, [getOrdemComprasDropDownUseCase]);

    const loadSubcontaDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getSubcontasDropDownUseCase.getSubcontasDropDown();
            setDdlSubconta(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar subcontas!'));
        } finally {
            setPageLoading(false);
        }
    }, [getSubcontasDropDownUseCase]);


    const loadFornecedorDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getFornecedorDropDownUseCase.getFornecedorDropDown();
            setDdlFornecedor(
                result.map((x) => ({
                    id: x.idFornecedor,
                    title: x.nomeFantasia
                }))
            );

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar fornecedores!'));
        } finally {
            setPageLoading(false);
        }
    }, [getFornecedorDropDownUseCase]);

    const loadFuncionarioDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
            setDdlFuncionario(result.map((x) => ({
                id: x.idFuncionario,
                title: x.nome
            })));

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar funcionários!'));
        } finally {
            setPageLoading(false);
        }
    }, [getFuncionariosDropDownUseCase]);

    const loadProdutoServicoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getProdutoServicoDropDownUseCase.getProdutoServicoDropDown();
            setDdlProdutoServico(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar produtos/serviços!'));
        } finally {
            setPageLoading(false);
        }
    }, [getCentroCustoDropDownUseCase]);

    const loadPgtoMedicaoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getMedicaoTeceirizacaoDropDownUseCase.getMedicaoTeceirizacaoDropDown();
            setDdlPgtoMedicao(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar pagamentos de medição!'));
        } finally {
            setPageLoading(false);
        }
    }, [getCentroCustoDropDownUseCase]);

    //#endregion





    usePageStartBootstrap(async () => {
        try {
            if (ContasPagarLista === undefined) {
                await loadContasPagarListaState();
                await loadCentroCustoDropDown();
                await loadBancosPagamentoDropDown();
                await loadOrdemCompraDropDown();
                await loadSubcontaDropDown();
                await loadFornecedorDropDown();
                await loadFuncionarioDropDown();
                await loadProdutoServicoDropDown();
                await loadPgtoMedicaoDropDown();

            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de contas a pagar!")[0]);
        }
    })




    //#region PAGINAÇÃO.

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadContasPagarListaState();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [loadContasPagarListaState]);

    const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
        setRowSelectionModel(rows);
    }, []);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadContasPagarListaState({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContasPagarListaState]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadContasPagarListaState({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar contas a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContasPagarListaState]);

    //#endregion





    //#region PESQUISA.

    const handleIdCentroCustoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsCentroCusto: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsCentroCusto: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasPagarListaState,]
    );

    const handleIdBancoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsBanco: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsBanco: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasPagarListaState,]
    );

    const handleIdOrdemCompraChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsOrdemCompra: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsOrdemCompra: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasPagarListaState,]
    );

    const handleIdSubcontaChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsSubconta: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsSubconta: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasPagarListaState,]
    );


    const handleTituloChange = useDebouncedCallback(
        debouncedRef,
        (titulo: string | null) => {
            filters.setFilters({ titulo });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ titulo, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasPagarListaState, filters]
    );

    const handleDataPagoRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataPagoDe: Date | null, dataPagoAte: Date | null) => {
            filters.setFilters({ dataPagoDe });
            filters.setFilters({ dataPagoAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ dataPagoDe, dataPagoAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasPagarListaState, filters, pagination]
    );

    const handleDataPagarRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataPagarDe: Date | null, dataPagarAte: Date | null) => {
            filters.setFilters({ dataPagarDe });
            filters.setFilters({ dataPagarAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ dataPagarDe, dataPagarAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasPagarListaState, filters, pagination]
    );

    const handleDataCriacaoRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataCriacaoDe: Date | null, dataCriacaoAte: Date | null) => {
            filters.setFilters({ dataCriacaoDe });
            filters.setFilters({ dataCriacaoAte });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ dataCriacaoDe, dataCriacaoAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasPagarListaState, filters]
    );

    const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number | null) => {
        useContasPagarListaFilters.setState({ status })
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadContasPagarListaState({ status, page: 1 });
            } finally {
                setLoading(false);
            }
        };
    }, [loadContasPagarListaState]);


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadContasPagarListaState({
                ...initialContasPagarFiltersValues,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar contas a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination, loadContasPagarListaState, pushErrorNotification]);


    const handleIdFornecedorChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsFornecedor: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsFornecedor: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleIdFuncionarioChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsFuncionario: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsFuncionario: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );


    const handleIdFuncionarioPagoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsFuncionarioPago: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsFuncionarioPago: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleIdProdutoServicoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsProdutoServico: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsProdutoServico: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleIdPgtoMedicaoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsPgtoMedicao: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsPgtoMedicao: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleValorChange = useDebouncedCallback(
        debouncedRef,
        (newValue: number | null) => {
            const valorNormalizado = newValue !== null && !isNaN(Number(newValue)) ? Number(newValue) : null;

            filters.setFilters({ valor: valorNormalizado });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ valor: valorNormalizado, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleNrContaChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            const valorNormalizado =
                newValue && !isNaN(Number(newValue)) && newValue.trim() !== ""
                    ? Number(newValue)
                    : null;

            filters.setFilters({ nrContasPagar: valorNormalizado });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ nrContasPagar: valorNormalizado, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleNotaFiscalChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            filters.setFilters({ nrNotaFiscal: newValue });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ nrNotaFiscal: newValue, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleNotaFiscalEspecialChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            filters.setFilters({ nrNotaFiscalEspecial: newValue });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ nrNotaFiscalEspecial: newValue, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleFormaPagamentoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            filters.setFilters({ formaPagamento: newValue });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ formaPagamento: newValue, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    //#endregion 





    //#region EXPORTAÇÃO.

    const handleContasPagarExportar = useCallback(async (idContapagar: number) => {
        try {
            setLoading(true);
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportContaPagarUseCase.execute(idContapagar, mimeType);
                }
            });

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de Conta a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [
        getReportContaPagarUseCase,
        pushErrorNotification,
    ]);

    //#endregion





    //#region  RECUPERAÇÃO

    const handleRecuperarContasPagar = useCallback(async () => {
        try {

            setSaving(true);
            if (rowSelectionModel) {
                const idsContasPagar: number[] = [];
                for (let row of rowSelectionModel.ids.values()) {
                    if (typeof row === 'number') {
                        idsContasPagar.push(row);
                    } else if (typeof row === 'string') {
                        try {
                            idsContasPagar.push(Number(row));
                        } catch (e) {
                        }
                    }
                }
                await recuperacaoContasPagarUseCase.recuperacaoContasPagar(idsContasPagar);
                pushInfoNotification("Sucesso", "Conta Recuperadas com sucesso!", { isAutoFinish: true });
            }

            await loadContasPagarListaState();

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Recuperar Contas!"));
        } finally {
            setSaving(false);
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        rowSelectionModel,
        recuperacaoContasPagarUseCase,
        loadContasPagarListaState,
        pushErrorNotification,
        pushInfoNotification]);

    //#endregion



    return {
        loading,
        saving,
        errorMessage,
        ContasPagarLista: ContasPagarLista ?? [],
        rowSelectionModel,
        filters,
        pagination,
        ddlCentroCusto: ddlCentroCusto ?? [],
        ddlBanco: ddlBanco ?? [],
        ddlOrdemCompra: ddlOrdemCompra ?? [],
        ddlSubconta: ddlSubconta ?? [],
        ddlFornecedor: ddlFornecedor ?? [],
        ddlFuncionario: ddlFuncionario ?? [],
        ddlProdutoServico: ddlProdutoServico ?? [],
        ddlPgtoMedicao: ddlPgtoMedicao ?? [],

        reportViewerState,
        handleIdFuncionarioPagoChange,
        handleRecuperarContasPagar,
        handleContasPagarExportar,
        handleValorChange,
        handleNrContaChange,
        handleNotaFiscalChange,
        handleNotaFiscalEspecialChange,
        handleFormaPagamentoChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleStatusChange,
        handleClearFilters,
        handleRowSelectionModelChange,
        handleIdCentroCustoChange,
        handleIdBancoChange,
        handleIdOrdemCompraChange,
        handleIdSubcontaChange,
        handleIdFornecedorChange,
        handleIdFuncionarioChange,
        handleIdProdutoServicoChange,
        handleIdPgtoMedicaoChange,
        handleTituloChange,
        handleDataPagarRangeChange,
        handleDataPagoRangeChange,
        handleDataCriacaoRangeChange,


    };

}
